/** Answer review screen — scrollable list of all answered steps with correct/incorrect indicators. */
import { motion } from 'motion/react';
import { ArrowLeft, Flag } from 'lucide-react';
import { PromptRenderer, type TiptapNode } from '@/shared/components/PromptRenderer';
import { toast } from 'sonner';
import type { StudySessionStep } from '@formency/shared';
import type { AnsweredStep } from './types';
import { stepTypeLabel, formatAnswer, ResultBadge } from './answerFormatting';
import { getGradingOutcome, OUTCOME_COLORS } from './sessionUtils';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Types ──────────────────────────────────────────────────────────────────

interface AnswerReviewScreenProps {
  steps: StudySessionStep[];
  answeredSteps: Map<number, AnsweredStep>;
  isPersonalLesson?: boolean;
  onBack: () => void;
}

// ─── Main component ─────────────────────────────────────────────────────────

export function AnswerReviewScreen({
  steps,
  answeredSteps,
  isPersonalLesson,
  onBack,
}: AnswerReviewScreenProps) {
  const handleFlag = (_stepIndex: number) => {
    // TODO: Send flag to API for review and regeneration
    toast.success('Flagged for review');
  };

  const promptDocs = steps.map(
    (step) => (step.stepSnapshotPublicJson?.prompt ?? null) as TiptapNode | null
  );

  return (
    <motion.div
      className="flex min-h-dvh flex-col"
      style={{ background: 'var(--f-bg)', fontFamily: 'var(--f-font-sans)' }}
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{ duration: 0.35, ease: EASE }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-3 border-b px-4 py-3 sm:px-8"
        style={{ borderColor: 'var(--f-border)', background: 'var(--f-bg)' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 font-medium transition-opacity hover:opacity-70"
          style={{ fontSize: 'var(--f-text-sm)', color: 'var(--f-fg-muted)' }}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <h2
          className="font-semibold"
          style={{
            fontSize: 'var(--f-text-lg)',
            color: 'var(--f-fg)',
            fontFamily: 'var(--f-font-display)',
            letterSpacing: 'var(--f-tracking-tight)',
          }}
        >
          Review Answers
        </h2>
      </div>

      {/* Card stack */}
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
        <div className="mx-auto max-w-2xl space-y-3">
          {steps.map((step, index) => {
            const answered = answeredSteps.get(index);
            const isInfo = step.stepKind === 'info';
            const promptDoc = promptDocs[index];

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: Math.min(index * 0.04, 0.4),
                  duration: 0.3,
                  ease: EASE,
                }}
                className="rounded-xl p-5"
                style={{
                  background: 'var(--f-bg-raised)',
                  border: '1px solid var(--f-border)',
                }}
              >
                {/* Top row: position + type + result badge */}
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-md font-semibold"
                      style={{
                        fontSize: 'var(--f-text-xs)',
                        background: 'var(--f-bg-sunken)',
                        color: 'var(--f-fg-muted)',
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ fontSize: 'var(--f-text-xs)', color: 'var(--f-fg-muted)' }}>
                      {stepTypeLabel(step.stepKind === 'info' ? 'info' : step.questionType)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {isPersonalLesson && !isInfo && (
                      <button
                        type="button"
                        onClick={() => handleFlag(index)}
                        className="flex items-center gap-1 rounded-md px-2 py-1 transition-opacity hover:opacity-70"
                        style={{
                          fontSize: 'var(--f-text-xs)',
                          color: 'var(--f-fg-muted)',
                          background: 'var(--f-bg-sunken)',
                        }}
                        title="Flag this question as incorrect or broken"
                      >
                        <Flag size={12} />
                        Flag
                      </button>
                    )}
                    <ResultBadge
                      isInfo={isInfo}
                      outcome={answered ? getGradingOutcome(answered.result) : undefined}
                    />
                  </div>
                </div>

                {/* Prompt */}
                {promptDoc && (
                  <div className="mb-3">
                    <PromptRenderer doc={promptDoc} className="f-prompt-prose" />
                  </div>
                )}

                {/* Answer + explanation */}
                {answered && !isInfo && (
                  <>
                    <div
                      className="rounded-lg px-3 py-2.5"
                      style={{
                        background: 'var(--f-bg-sunken)',
                        fontSize: 'var(--f-text-sm)',
                        lineHeight: 'var(--f-leading-relaxed)',
                        color: 'var(--f-fg-secondary)',
                      }}
                    >
                      <span style={{ color: 'var(--f-fg-muted)', fontWeight: 500 }}>
                        Your answer:{' '}
                      </span>
                      {formatAnswer(step, answered)}
                    </div>

                    {(answered.result.explanation || answered.result.feedback) && (
                      <div
                        className="mt-2 rounded-lg px-3 py-2.5"
                        style={{
                          fontSize: 'var(--f-text-sm)',
                          lineHeight: 'var(--f-leading-relaxed)',
                          ...OUTCOME_COLORS[getGradingOutcome(answered.result)],
                        }}
                      >
                        {answered.result.explanation && <p>{answered.result.explanation}</p>}
                        {answered.result.feedback && (
                          <p className={answered.result.explanation ? 'mt-1' : ''}>
                            {answered.result.feedback}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
